import { FormattedMessage } from 'react-intl';
import IconChevronBottomOutlined from '@repo/monochrome-icons/icon-chevron-bottom-outlined';
import { Avatar, getInitials } from '@repo/design-system-kit/components/avatar/avatar';
import { Button as DsButton } from '@repo/design-system-kit/components/button/button';
import { Menu } from '@repo/design-system-kit/components/menu/menu';
import { useSegment } from '@repo/poly-hooks/segment';
import { type ReactNode, useContext } from 'react';
import cx from 'clsx';
import { webviewManager } from '@repo/runtime-kit/webview';
import { CurrentOrganizationIdContext } from '@repo/runtime-kit/organizations/contexts/current-organization-id';
import { useCurrentOrganizationId } from '@repo/runtime-kit/organizations/hooks/use-current-organization-id';
import { useOrganizations } from '@repo/runtime-kit/organizations/hooks/use-organizations';
import { useSuspenseQuery } from '@tanstack/react-query';
import { registerOrganizationQueries } from '@repo/api-kit/queries/register-organization';
import { useSession } from '@repo/poly-hooks/session';
import { appJsURL, companyCreationJsURL } from '@repo/runtime-kit/constants/hosts';
import { computeIsAssociation } from '@repo/api-kit/register-organization/utils';
import styles from './dropdown-organization.module.css';

export function DropdownOrganization({ className }: { className?: string }): ReactNode {
  const session = useSession();

  const organizations = useOrganizations();

  // the useCurrentOrganizationId hook was initially built for qonto-js, where we can always assume there is a current organization
  // this is not always true in onboarding apps.
  const orgId = useContext(CurrentOrganizationIdContext);

  const orgName = organizations.find(org => org.id === orgId)?.name;

  const segment = useSegment();

  const handleDropdownSignOut = (): void => {
    segment.track('onboarding_dropdown_organization_sign_out_clicked');
    void session.invalidate();
  };

  const handleSignOut = (): void => {
    segment.track('onboarding_success_sign_out_clicked');
    void session.invalidate();
  };

  // When there is nothing to switch to, a dropdown that only contains "Sign out" is a poor UX.
  // We instead display the sign out action directly in the sidebar footer.
  const shouldRenderDropdown = organizations.length > 0;

  const handleDropdownOpen = (open: boolean): void => {
    if (open) {
      segment.track('onboarding_dropdown_organization_opened');
    }
  };

  if (!shouldRenderDropdown) {
    return (
      <div className={className} data-test-dropdown-organization>
        <DsButton data-test-signout onClick={handleSignOut} type="button" variant="tertiary">
          <FormattedMessage id="generic.sign-out.cta" />
        </DsButton>
      </div>
    );
  }

  return (
    <div className={className} data-react-aria-top-layer data-test-dropdown-organization>
      <Menu onOpenChange={handleDropdownOpen}>
        <Menu.Button
          className={cx(styles.trigger, 'body-standard')}
          data-test-dropdown-organization-trigger
        >
          {orgId ? (orgName ?? <NewOrganizationName />) : <DefaultOrganizationName />}
          <IconChevronBottomOutlined className={styles.triggerIcon} />
        </Menu.Button>
        <Menu.Content
          afterContent={
            <button
              className={styles.signOutButton}
              data-test-signout
              onClick={handleDropdownSignOut}
              type="button"
            >
              <FormattedMessage id="generic.sign-out.cta" />
            </button>
          }
        >
          {organizations.map((organization, index) => (
            <Menu.Item
              beforeContent={
                <Avatar
                  type="image"
                  src={organization.avatarThumb?.fileUrl}
                  fallbackInitials={getInitials(organization.name)}
                  size="x-small"
                />
              }
              data-test-dropdown-organization-id={organization.id}
              data-test-menu-item={index}
              key={organization.id}
              onAction={() => {
                if (webviewManager.isWebview) {
                  webviewManager.onFlowFinished(organization.id);
                } else {
                  window.location.replace(
                    // Only non-FR orgs under company creation live on the (DE) company-creation
                    // app. FR orgs under company creation are served by the main app, like any
                    // other org. Keep this in sync with organization-manager's list split.
                    organization.underCompanyCreation && organization.legalCountry !== 'FR'
                      ? `${companyCreationJsURL}?organization=${organization.id}`
                      : `${appJsURL}/organizations/${organization.slug}`
                  );
                }
              }}
            >
              {organization.name}
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu>
    </div>
  );
}

function NewOrganizationName() {
  const orgId = useCurrentOrganizationId();
  const currentOrganization = useSuspenseQuery(registerOrganizationQueries.item(orgId)).data;

  const isAssociationOrg = computeIsAssociation(currentOrganization);

  if (currentOrganization.name) {
    return currentOrganization.name;
  }

  return isAssociationOrg ? (
    <FormattedMessage id="shared-kit.dropdown-organization.new-association" />
  ) : (
    <DefaultOrganizationName />
  );
}

function DefaultOrganizationName() {
  return <FormattedMessage id="shared-kit.dropdown-organization.new-organization" />;
}
